// Oud / Arabic–Turkish lofi, Cairo to Istanbul (docs/AUDIO_DESIGN.md §12): an oud plucked with a plectrum plays drones,
// open fifths and triads in a maqam (Hijaz or Ajam in major keys, Nahawand or Kurd in minor, in 12-TET on the key's own
// tonic), sliding into notes and trembling long ones (the rish); its low courses over a soft sub carry the bass; a ney
// breathes the melody with turns and trills in the maqam; a darbuka (doum, tek, ka) and a riq play maqsum, baladi, saidi,
// çiftetelli and wahda under the tape haze.
import { rng } from '../../engine/core.js';
import { QUALITIES, bassNote, expand } from '../harmony.js';
import { createOud, createOudBass } from './oud-voice.js';
import { createNey, createDarbuka } from './oud-band.js';

const mod12 = n => ((n % 12) + 12) % 12;

/* ---------------- maqamat (12-TET, on the key's tonic) ---------------- */
// scale: the maqam's degrees (the ney's ornaments, the oud's runs). map: how the ney bends the engine's pentatonic melody into
// it (semitones added per degree): Hijaz lowers the major pentatonic's 2nd and 6th (1 ♭2 3 5 ♭6: the augmented second ♭2–3),
// Kurd turns the minor pentatonic's ♭7 into the ♭6 (1 ♭3 4 5 ♭6); Ajam and Nahawand keep it (their colour is in the harmony
// and the ornaments: Nahawand's leading tone under the tonic, its major V). lead: the bass's leading note into the tonic.
export const MAQAMS = {
  hijaz: { scale: [0, 1, 4, 5, 7, 8, 10], map: { 2: -1, 9: -1 }, lead: 1 },
  ajam: { scale: [0, 2, 4, 5, 7, 9, 11], map: {}, lead: -1 },
  nahawand: { scale: [0, 2, 3, 5, 7, 8, 10], map: {}, lead: -1 },
  kurd: { scale: [0, 1, 3, 5, 7, 8, 10], map: { 10: -2 }, lead: 1 },
};
const FAMILY = { h: 'hijaz', a: 'ajam', n: 'nahawand', k: 'kurd' };

/* ---------------- harmony: triads, open fifths and sus chords, a progression family per maqam ---------------- */
// oud = major triad, oudm = minor, oud5 = open fifth, oudsus4. The first letter of a progression's id is its maqam; progB
// stays in progA's (plan()), and reusing progA ends on the maqam's own turnaround bar.
const QUAL = { oud: [0, 4, 7], oudm: [0, 3, 7], oud5: [0, 7, 12], oudsus4: [0, 5, 7] };
const BANK = {
  major: {
    h1: [[0, 'oud', 4], [1, 'oud', 4], [0, 'oud', 4], [0, 'oud5', 4]],                                // I · ♭II · I · I5
    h2: [[0, 'oud', 4], [10, 'oudm', 4], [1, 'oud', 4], [0, 'oud', 4]],                               // I · ♭vii · ♭II · I
    h3: [[5, 'oudm', 4], [1, 'oud', 4], [0, 'oud', 4], [0, 'oud5', 4]],                               // iv · ♭II · I · I5
    h4: [[0, 'oud5', 4], [5, 'oudm', 4], [1, 'oud', 2], [10, 'oudm', 2], [0, 'oud', 4]],              // I5 · iv · ♭II|♭vii · I
    h5: [[0, 'oud', 4], [0, 'oudsus4', 2], [0, 'oud', 2], [1, 'oud', 4], [0, 'oud', 4]],              // I · Isus4|I · ♭II · I
    a1: [[0, 'oud', 4], [5, 'oud', 4], [7, 'oudsus4', 2], [7, 'oud', 2], [0, 'oud', 4]],              // I · IV · Vsus4|V · I
    a2: [[0, 'oud', 4], [7, 'oud', 4], [5, 'oud', 4], [0, 'oud', 4]],                                 // I · V · IV · I
    a3: [[0, 'oud5', 4], [9, 'oudm', 4], [5, 'oud', 4], [0, 'oud', 4]],                               // I5 · vi · IV · I
  },
  minor: {
    n1: [[0, 'oudm', 4], [10, 'oud', 4], [8, 'oud', 4], [7, 'oud', 4]],                               // i · ♭VII · ♭VI · V (Andalusian)
    n2: [[0, 'oudm', 4], [5, 'oudm', 4], [7, 'oud', 4], [0, 'oudm', 4]],                              // i · iv · V · i
    n3: [[0, 'oud5', 4], [8, 'oud', 4], [7, 'oud', 4], [0, 'oudm', 4]],                               // i5 · ♭VI · V · i
    n4: [[0, 'oudm', 4], [8, 'oud', 4], [5, 'oudm', 2], [7, 'oud', 2], [0, 'oudm', 4]],               // i · ♭VI · iv|V · i
    k1: [[0, 'oudm', 4], [1, 'oud', 4], [0, 'oudm', 4], [0, 'oud5', 4]],                              // i · ♭II · i · i5
    k2: [[0, 'oudm', 4], [8, 'oud', 4], [10, 'oudm', 4], [0, 'oudm', 4]],                             // i · ♭VI · ♭vii · i
    k3: [[5, 'oudm', 4], [1, 'oud', 4], [0, 'oudm', 4], [0, 'oud5', 4]],                              // iv · ♭II · i · i5
    k4: [[0, 'oudm', 4], [3, 'oud', 4], [1, 'oud', 4], [0, 'oudm', 4]],                               // i · ♭III · ♭II · i
  },
};
// the bar that ends progB when it reuses progA: ♭II|I (Hijaz), IV|Vsus4 (Ajam), iv|V (Nahawand), ♭II|i (Kurd)
const TURN = { h: [[1, 'oud', 2], [0, 'oud', 2]], a: [[5, 'oud', 2], [7, 'oudsus4', 2]], n: [[5, 'oudm', 2], [7, 'oud', 2]], k: [[1, 'oud', 2], [0, 'oudm', 2]] };

/* ---------------- oud voicings: a bass course + 3 upper courses, low and open ---------------- */
// Tones: the quality's triad (oud5: 5-R-5 or R-5-R). The engine's final tonic (m9 / maj9 / 69) is the key's triad. Upper
// courses: every order of the tones stacked upward from MIDI 50–62, top 57–69, span ≤ 12, neighbours ≥ a whole step apart
// (≥ a minor 3rd below MIDI 55), ≥ a 4th above a bass under 43 (else ≥ a minor 3rd); the bass course is the root at MIDI
// 36–48 or, on pedal tracks, the tonic under ♭II, iv, ♭VI and ♭VII / ♭vii (the Phrygian drone of Hijaz and Kurd).
const TONES = { oud: [0, 4, 7], oudm: [0, 3, 7], oud5: [0, 7], oudsus4: [0, 5, 7] };
const PEDAL = new Set([1, 5, 8, 10]);
const tonesOf = (c, plan) => {
  if (TONES[c.q]) return TONES[c.q];
  if (c.final) return plan.key.mode === 'minor' ? TONES.oudm : TONES.oud;
  const iv = QUALITIES[c.q] || TONES.oud;
  return iv.includes(4) ? TONES.oud : iv.includes(3) ? TONES.oudm : TONES.oud5;
};
/** the bass course's pitch class: the root, or the tonic under a pedalled chord */
const bassPc = (c, plan) => mod12(plan.key.tonic + (plan.oud.pedal && !c.final && PEDAL.has(mod12(c.root)) ? 0 : c.root));
const ORDERS = { 3: [[0, 1, 2], [0, 2, 1], [1, 0, 2], [1, 2, 0], [2, 0, 1], [2, 1, 0]], 2: [[1, 0, 1], [0, 1, 0]] };
const CANDS = new Map();                                     // `${root}:${bass}:${tones}` → [[bass, u0, u1, u2]]
function candidates(root, bpc, tones) {
  const key = `${root}:${bpc}:${tones}`;
  if (CANDS.has(key)) return CANDS.get(key);
  const pcs = tones.map(i => mod12(root + i)), out = [], basses = [];
  for (let m = 36 + mod12(bpc - 36); m <= 48; m += 12) basses.push(m);
  for (const order of ORDERS[tones.length]) for (let u0 = 50 + mod12(pcs[order[0]] - 50); u0 <= 62; u0 += 12) {
    const u = [u0];
    for (let k = 1; k < 3; k++) { let m = u[k - 1] + 2; m += mod12(pcs[order[k]] - m); u.push(m); }
    if (u[2] < 57 || u[2] > 69 || u[2] - u[0] > 12 || (u[0] < 55 && u[1] - u[0] < 3)) continue;
    for (const b of basses) if (u[0] - b >= (b < 43 ? 5 : 3)) out.push([b, ...u]);
  }
  CANDS.set(key, out);
  return out;
}
/** Cost: Σ|Δ| of the upper courses + .4·|Δbass| + 2·max(0, |Δtop| − 3) + .5·|mean(upper) − home| + .3·|bass − 42|, home =
 *  60 + plan.register (−5..+5, so tracks don't all open on the same notes). The same chord keeps its voicing. */
export function oudVoicing(sp, prev, c, plan) {
  const root = mod12(plan.key.tonic + c.root), tones = tonesOf(c, plan), bpc = bassPc(c, plan), home = 60 + (plan.register || 0);
  if (prev && prev.length === 4 && mod12(prev[0]) === bpc) {
    const want = new Set(tones.map(i => mod12(root + i))), have = new Set(prev.slice(1).map(mod12));
    if (want.size === have.size && [...want].every(pc => have.has(pc))) return prev;
  }
  let best = null, bc = Infinity;
  for (const x of candidates(root, bpc, tones)) {
    const [b, u0, u1, u2] = x;
    let k = 0.5 * Math.abs((u0 + u1 + u2) / 3 - home) + 0.3 * Math.abs(b - 42);
    if (prev && prev.length === 4) k += Math.abs(u0 - prev[1]) + Math.abs(u1 - prev[2]) + Math.abs(u2 - prev[3]) + 0.4 * Math.abs(b - prev[0]) + 2 * Math.max(0, Math.abs(u2 - prev[3]) - 3);
    if (k < bc) { bc = k; best = x; }
  }
  return best || [36 + mod12(bpc - 36), ...tones.concat([12]).slice(0, 3).map(i => 55 + mod12(root + i - 55)).sort((a, b) => a - b)];
}

/* ---------------- bass: the oud's low courses on the doums ---------------- */
// A root (the tonic on pedal tracks, as the oud's bass course) on each chord's first step and on the doums (kick steps) of
// the bar, the fifth on a later doum (p .3), a rest on a later doum (p plan.oud.rest); on the step 14 before a return to the
// tonic the maqam's leading note slides in (the ♭2 above in Hijaz and Kurd, the leading tone below in Ajam and Nahawand, p
// .3). The break and the intro's last bar hold whole roots, the final one rings 28 steps; a fill's stop cuts the line.
function oudBass({ plan, st, b, mode, chords, nextChords, kicks, stop }) {
  const O = plan.oud, out = [], end = stop ?? 16, f = plan.bass.factor;
  const add = (s, midi, vel, dur, slide = false) => { if (s < end) out.push({ s, midi, vel, dur: Math.min(dur, end - s), slide }); };
  if (mode === 'whole' || mode === 'last' || (mode === 'outro' && b.j === 2)) {
    for (const c of chords) { const m = bassNote(bassPc(c, plan), st.prevBass); add(c.start * 4, m, 0.78, mode === 'outro' ? 28 : c.beats * 4 * 0.96); st.prevBass = m; }
    return out;
  }
  chords.forEach(c => {
    const c0 = c.start * 4, c1 = c0 + c.beats * 4, root = bassNote(bassPc(c, plan), st.prevBass), iv = QUALITIES[c.q] || [0, 7];
    const fifth = iv.includes(7) && bassPc(c, plan) === mod12(plan.key.tonic + c.root) ? (root + 7 <= 47 ? root + 7 : root - 5) : root;
    const steps = [...new Set([c0, ...kicks.filter(s => s > c0 && s < c1)])].sort((x, y) => x - y);
    steps.forEach((s, k) => {
      const dur = ((k + 1 < steps.length ? steps[k + 1] : c1) - s) * f;
      if (k === 0) { add(s, root, c.onset === false ? 0.66 : 0.86, dur, st.rS() < 0.12); return; }
      const u = st.rS();
      if (u >= O.rest) add(s, u < O.rest + 0.3 * (1 - O.rest) ? fifth : root, 0.7, dur);
    });
    st.prevBass = root;
  });
  const next = nextChords && nextChords[0], last = chords[chords.length - 1];
  if (next && mod12(next.root) === 0 && mod12(last.root) !== 0 && end > 14 && st.rS() < 0.3) {
    const target = bassNote(plan.key.tonic, st.prevBass), m = target + MAQAMS[O.maqam].lead;
    for (const n of out) if (n.s < 14) n.dur = Math.min(n.dur, 14 - n.s);
    const k = out.findIndex(n => n.s >= 14);
    if (k >= 0) out.splice(k);
    add(14, m, 0.64, 2, true);
  }
  return out;
}

/* ---------------- melody: the ney's motif (long notes, stepwise, falling towards the tonic) ---------------- */
// Rhythm cells (16th steps over 2 bars), few notes on beats 1 and 3 (where the engine snaps them to chord tones, mostly
// the tonic in these drone-like progressions), each held to the next (a step early p .35, so some are tongued rather than
// slurred), the last one 6–11 steps; the contour moves a step (p .7), two (p .22) or leaps a 3rd and steps back, turning
// round past ±3, falling first on 60 % of tracks. The engine still picks the notes (the key's pentatonic); the ney bends
// them into the maqam and ornaments them.
const NCELLS = [[0, 3, 6, 10], [2, 6, 10, 12], [0, 5, 10], [3, 6, 8, 12], [0, 2, 6, 10], [0, 6, 12, 14], [2, 4, 10]];
const NTAILS = [[], [18], [18, 22], [20], [16, 22]];
function neyMotif(r, key) {
  const steps = NCELLS[Math.floor(r() * NCELLS.length)].concat(NTAILS[Math.floor(r() * NTAILS.length)]).slice(0, 6), n = steps.length;
  const last = 6 + Math.floor(r() * 6);
  const durs = steps.map((s, i) => (i < n - 1 ? Math.max(1, Math.min(8, steps[i + 1] - s) - (r() < 0.35 ? 1 : 0)) : last));
  const contour = [0];
  let dir = r() < 0.6 ? -1 : 1, back = 0;
  for (let i = 1; i < n; i++) {
    const u = r();
    let mv;
    if (back) { mv = -back; back = 0; } else if (u < 0.7) mv = dir; else if (u < 0.92) mv = 2 * dir; else { mv = 2 * dir; back = dir; }
    contour.push(contour[i - 1] + mv);
    if (Math.abs(contour[i]) >= 3) dir = -Math.sign(contour[i]);
  }
  const vel = steps.map((s, i) => (i === 0 ? 0.78 : s % 8 === 0 ? 0.72 : 0.64 + 0.06 * r()));
  return { steps, durs, contour, vel, mode: key.mode };
}

/* ---------------- comping: oud cells of [step, length, velocity, pick] ---------------- */
// Voicing indices: 0 = the bass course, 1–3 = the upper courses (-1 = the top). The rhythmic cells follow the darbuka: the
// bass on the doums, double stops on the teks. One or two notes held ≥ 5 steps may tremble (the instrument decides).
const T = [0], TOP = [-1], TWO = [-2, -1];
const COMPS = {
  rish: [[0, 4, 1, [0, 1]], [4, 12, 0.78, TOP]],                                                      // bass + a course, then the top trembles
  slow: [[0, 8, 1], [8, 8, 0.72, [0, -1]]],                                                           // a stroke, then bass + top on 3
  drone: [[0, 16, 0.9, [0, 2]]],                                                                      // bass + a middle course, left to ring
  arp: [[0, 4, 1, T], [2, 2, 0.7, [1]], [4, 4, 0.74, [2]], [6, 2, 0.7, [3]], [8, 4, 0.86, T], [10, 2, 0.7, [3]], [12, 2, 0.74, [2]], [14, 2, 0.66, [1]]],
  maqsum: [[0, 2, 1, T], [2, 4, 0.72, TWO], [6, 2, 0.8, TWO], [8, 4, 0.9, [0, 2]], [12, 4, 0.76, TWO]],            // D T . T D . T .
  baladi: [[0, 2, 1, T], [2, 4, 0.86, T], [6, 2, 0.8, TWO], [8, 4, 0.9, [0, 2]], [12, 4, 0.76, TWO]],             // D D . T D . T .
  saidi: [[0, 2, 1, T], [2, 4, 0.72, TOP], [6, 2, 0.86, T], [8, 4, 0.9, [0, 2]], [12, 4, 0.76, TWO]],             // D T . D D . T .
};

/* ---------------- drums: darbuka + riq, straight ---------------- */
// K doum · S tek · R ka (a run of kas rolls) · H riq jingles (o = a shake) · X the riq rolled · T doholla (a bass darbuka)
const GRIDS = {
  //          0123456789abcdef
  maqsum: { K: 'x.......x.......', S: '..x...x.....x...', R: '....s.....s...s.', H: 'x.s.a.s.x.s.a.s.' },      // D T . T D . T .
  baladi: { K: 'x.x.....x.......', S: '......x.....x...', R: '....s.....s.s.s.', H: 'x.s.s.a.x.s.a.s.' },      // D D . T D . T .
  saidi: { K: 'x.....x.x.......', S: '..x.........x...', R: '....s.....s...s.', H: 'x.s.s.s.x.s.a.s.' },       // D T . D D . T .
  cifte: { K: 'x.......x.x.....', S: '...x.x......x...', R: '.s.....s.....s..', H: 'x...s...x...s...' },       // çiftetelli D..T.T..D.D.T...
  wahda: { K: 'x.........s.....', S: '........x.......', R: '....s.......s.s.', H: 's.......s.......' },       // one doum, a tek on 3
  ayyoub: { K: 'x...x...x...x...', S: '......x.......x.', R: '...s.......s....', H: 'x.s.x.s.x.s.x.s.' },      // D . k D T . (twice): zar
  break: { R: '....s.......s...', H: 's.s.s.s.s.s.s.s.', X: 'x.......x.......' },
};
const GROOVES = {
  chill: { wahda: 1.6, cifte: 1, maqsum: 0.5 },
  slow: { maqsum: 1.3, cifte: 1, wahda: 0.6, baladi: 0.6 },
  fast: { maqsum: 1.3, baladi: 1, saidi: 0.9, cifte: 0.5 },
  upbeat: { maqsum: 1.1, saidi: 1.1, baladi: 1, ayyoub: 0.45 },
};

export default {
  id: 'oud', name: 'Oud', desc: 'Oud, ney flute and darbuka in Middle Eastern maqam scales',
  //            chill                          balanced                       upbeat
  vary: 'half',
  bridge: false,
  energy: {
    stretch:    [0.5,                          0.35,                          0.2],
    bpm:        [[66, 74],                     [72, 84],                      [80, 92]],
    swing:      [[51, 54],                     [50, 53],                      [50, 52]],
    snareLag:   [[0.004, 0.01],                [0.002, 0.007],                [0, 0.004]],
    groove:     [[GROOVES.chill, GROOVES.chill], [GROOVES.slow, GROOVES.fast], [GROOVES.upbeat, GROOVES.upbeat]],
    drumLevel:  [0.8,                          0.85,                          0.9],
    kitLp:      [[4800, 6800],                 [5600, 8000],                  [6400, 9000]],
    epLp:       [[1900, 2700],                 [2200, 3200],                  [2600, 3800]],     // the oud bus low-pass
    strum:      [[0.02, 0.04],                 [0.014, 0.03],                 [0.01, 0.022]],    // one plectrum stroke across the courses
    epVel:      [[0.55, 0.65],                 [0.56, 0.66],                  [0.56, 0.68]],
    comp: [
      { rish: [0.8, 1.4], slow: [0.5, 1], drone: [0.4, 0.9], arp: [0.2, 0.6], maqsum: [0, 0.15], baladi: [0, 0], saidi: [0, 0] },
      { rish: [0.4, 0.9], slow: [0.15, 0.4], drone: [0.1, 0.3], arp: [0.4, 1], maqsum: [0.5, 1.1], baladi: [0.2, 0.6], saidi: [0.2, 0.6] },
      { rish: [0.2, 0.6], slow: [0, 0.15], drone: [0, 0.1], arp: [0.4, 0.9], maqsum: [0.7, 1.3], baladi: [0.4, 0.9], saidi: [0.5, 1] },
    ],
    push:       [0.04,                         0.08,                          0.14],
    legato:     [0.75,                         0.6,                           0.45],
    lead:       [0.75,                         0.82,                          0.88],
    softLead:   [0.7,                          0.6,                           0.5],              // odds of the ney over the kawala
    tone:       [[4200, 6200],                 [5000, 7500],                  [6000, 8500]],
    wowCents:   [[5, 10],                      [4, 8],                        [3, 7]],
    dust:       [[0.9, 1.5],                   [0.7, 1.3],                    [0.6, 1.1]],
    introHats:  [0.35,                         0.5,                           0.65],
    leadFrom:   [2,                            2,                             1],
    hatsFirst:  [0.4,                          0.3,                           0.2],
    breakDrums: [0.4,                          0.6,                           0.8],
    breakLead:  [0.4,                          0.5,                           0.6],
    trem:       [[0.5, 0.8],                   [0.35, 0.65],                  [0.25, 0.5]],      // odds a long top note trembles (rish)
    slide:      [[0.15, 0.3],                  [0.2, 0.35],                   [0.2, 0.4]],       // odds a single note slides in
    flourish:   [[0.35, 0.6],                  [0.3, 0.55],                   [0.25, 0.5]],      // odds a held chord opens with a run
    ornament:   [[0.2, 0.32],                  [0.24, 0.36],                  [0.26, 0.4]],      // odds of a ney ornament per note
    pedal:      [0.6,                          0.45,                          0.3],              // odds of a tonic pedal under ♭II, iv, ♭VI, ♭VII
    bassRest:   [[0.3, 0.45],                  [0.2, 0.35],                   [0.1, 0.25]],      // odds a later doum has no bass
    // development (arrange.js BY_ENERGY): no lift (the maqam); a taqsim-like solo; the ney first
    lift:       [0, 0, 0],
    solo:       [0.2, 0.25, 0.3],
    intro:      [{ keys: 0.3, beat: 0.15, groove: 0.1, lead: 0.4, cold: 0.05 }, { keys: 0.3, beat: 0.15, groove: 0.1, lead: 0.4, cold: 0.05 }, { keys: 0.3, beat: 0.15, groove: 0.1, lead: 0.4, cold: 0.05 }],
  },
  qualities: QUAL,
  progressions: BANK,
  turnarounds: { major: null, minor: null },               // progB's turnaround is the maqam's own (plan())
  grids: GRIDS,
  fills: {
    roll: { roll: [[12, 0.42], [13, 0.5], [14, 0.62], [15, 0.76]], voice: 'rim' },   // a ka roll (the kit doubles it: trrr)
    doholla: { kicks: [11, 14], voice: 'tom' },                                       // the bass darbuka's pickups
    stop: { stop: 12 },                                                               // the band stops, the oud rings on
    shake: { open: 14 },                                                              // a riq shake
  },
  comps: COMPS,
  keysLevel: 1.65,

  plan(plan, { r, E }) {
    const rr = (a, b, d = 3) => +(a + r() * (b - a)).toFixed(d);
    const bank = BANK[plan.key.mode], idA = plan.progA.id, fam = idA[0], M = MAQAMS[FAMILY[fam]];
    // progB stays in progA's maqam: progA again with the maqam's turnaround, or another progression of its family
    if (plan.progB.id === idA) plan.progB = { id: `${idA}+turn`, ...expand(bank[idA], { stretch: plan.chordBars, turnaround: TURN[fam] }) };
    else if (plan.progB.id[0] !== fam) {
      const others = Object.keys(bank).filter(id => id[0] === fam && id !== idA), idB = others[Math.floor(r() * others.length)];
      plan.progB = { id: idB, ...expand(bank[idB], { stretch: plan.chordBars }) };
      for (const k of Object.keys(plan.vars)) if (k[0] === 'B') delete plan.vars[k];   // B's passes were of the other maqam
    }
    plan.oud = {
      maqam: FAMILY[fam], scale: M.scale, map: Array.from({ length: 12 }, (_, d) => M.map[d] || 0), pedal: r() < E.pedal,
      trem: rr(...E.trem), slide: rr(...E.slide), flourish: rr(...E.flourish), orn: rr(...E.ornament), rest: rr(...E.bassRest),
    };
    plan.oudBass = { lp: rr(800, 1200, 0) };
    const k = plan.drums.kit;
    plan.drums.kit = {
      doumF0: rr(96, 112, 1), doumF1: rr(64, 74, 1), doumDecay: rr(0.2, 0.3), tekBP: rr(2900, 3900, 0), tekPing: rr(620, 780, 0),
      tekDecay: rr(0.016, 0.026, 4), jingle: rr(0.04, 0.065, 4), lp: k.lp,
    };
    // the oud follows the groove: the drums' own cell (maqsum, baladi, saidi) weighs more
    const cw = plan.ep.comp, g = plan.drums.pattern;
    if (cw[g] != null) cw[g] = +(cw[g] * 2.5 + 0.3).toFixed(2);
    if (plan.lead) Object.assign(plan.lead, { timbre: plan.lead.timbre === 'soft' ? 'ney' : 'kawala', motif: neyMotif(r, plan.key) });
  },

  voicing: oudVoicing,
  bass: oudBass,
  voices(ac, dry, wet, plan, ctx) {
    const o = { registry: ctx.registry, tag: ctx.tag, t0: ctx.t0 }, sub = () => rng(Math.floor(ctx.r() * 4294967296));
    const ep = createOud(ac, dry, wet, plan, { ...o, cap: ctx.cap, r: sub(), log: ctx.log, stepDur: ctx.stepDur });
    ep.warm(trackNotes(plan), 3);
    const lead = plan.lead ? createNey(ac, dry, wet, plan, { ...o, stepDur: ctx.stepDur, r: sub() }) : null;
    return {
      ep, lead, bass: createOudBass(ac, dry, plan.oudBass, o),
      kit: createDarbuka(ac, dry, wet, plan.drums.kit, { ...o, stepDur: ctx.stepDur }),
    };
  },
};

/** the track's chord notes, first bars first (progA, progB, the final tonic as in engine.js barChords), then its bass
 *  roots at both octaves, to render their plucks ahead of time */
function trackNotes(plan) {
  const out = [], roots = [], fin = { root: 0, q: plan.key.mode === 'minor' ? 'm9' : 'maj9', final: true };
  let prev = null;
  for (const c of [...plan.progA.chords, ...plan.progB.chords, fin]) {
    prev = oudVoicing(null, prev, c, plan); out.push(...prev);
    const pc = bassPc(c, plan);
    for (let m = 33 + mod12(pc - 33); m <= 47; m += 12) roots.push(m);
  }
  return [...out, ...roots];
}
